/**
 * Parent Origin Detection Utility
 * 
 * Detects the parent domain (client's website) where the widget is embedded.
 * This is used to send to the backend for domain allowlist validation.
 * 
 * Supports all 3 embedding modes:
 * - Embed: iframe on client's page
 * - Popup: modal overlay on client's page
 * - Page: new window opened from client's page
 */

/**
 * Get the parent origin (client's domain) where the widget is embedded
 * 
 * Priority order:
 * 1. Stored parent origin (from postMessage - most reliable for popup/page modes)
 * 2. document.referrer (works for all modes, set by browser)
 * 3. window.opener.location.origin (for page mode, may be blocked by CORS)
 * 
 * @returns Parent origin (e.g., "https://clinic.com") or null if not available
 */
export const getParentOrigin = (): string | null => {
  // Priority 1: Stored parent origin (from postMessage - most reliable)
  // This is set when the loader sends WIDGET_INIT message with parentOrigin
  if (typeof window !== 'undefined' && (window as any).__WIDGET_PARENT_ORIGIN__) {
    const stored = (window as any).__WIDGET_PARENT_ORIGIN__;
    // Use console.warn so it persists in production (console.log is removed)
    console.warn('🌐 [ParentOrigin] Using stored parent origin:', stored);
    // Expose to window for debugging (always available)
    if (typeof window !== 'undefined') {
      (window as any).__WIDGET_DEBUG_PARENT_ORIGIN__ = stored;
    }
    return stored;
  }
  
  // Priority 2: document.referrer (works for all modes)
  // Browser automatically sets this to the parent page URL
  if (document.referrer) {
    try {
      const referrerOrigin = new URL(document.referrer).origin;
      const widgetOrigin = window.location.origin;
      
      // Validate it's not the widget's own domain
      if (referrerOrigin !== widgetOrigin) {
        console.warn('🌐 [ParentOrigin] Using document.referrer:', referrerOrigin);
        // Expose to window for debugging
        if (typeof window !== 'undefined') {
          (window as any).__WIDGET_DEBUG_PARENT_ORIGIN__ = referrerOrigin;
        }
        return referrerOrigin;
      } else {
        console.warn('🌐 [ParentOrigin] document.referrer is widget domain, ignoring');
      }
    } catch (e) {
      console.warn('🌐 [ParentOrigin] Invalid referrer URL:', document.referrer, e);
    }
  }
  
  // Priority 3: Try to get from window.opener (for page mode)
  // This might be blocked by CORS, but worth trying
  if (window.opener && window.opener !== window) {
    try {
      const openerOrigin = window.opener.location.origin;
      console.warn('🌐 [ParentOrigin] Using window.opener:', openerOrigin);
      // Expose to window for debugging
      if (typeof window !== 'undefined') {
        (window as any).__WIDGET_DEBUG_PARENT_ORIGIN__ = openerOrigin;
      }
      return openerOrigin;
    } catch (e) {
      // CORS blocked - can't access opener's origin
      console.warn('🌐 [ParentOrigin] Cannot access window.opener.location (CORS blocked)');
    }
  }
  
  console.warn('🌐 [ParentOrigin] Could not determine parent origin');
  // Clear debug value if no origin found
  if (typeof window !== 'undefined') {
    (window as any).__WIDGET_DEBUG_PARENT_ORIGIN__ = null;
  }
  return null;
};

/**
 * Store parent origin globally (called when received via postMessage)
 * 
 * @param origin - Parent origin to store (e.g., "https://clinic.com")
 */
export const setParentOrigin = (origin: string): void => {
  if (typeof window !== 'undefined') {
    (window as any).__WIDGET_PARENT_ORIGIN__ = origin;
    // Use console.warn so it persists in production
    console.warn('✅ [ParentOrigin] Stored parent origin:', origin);
    // Expose to window for debugging (always available)
    (window as any).__WIDGET_DEBUG_PARENT_ORIGIN__ = origin;
  }
};

/**
 * Clear stored parent origin (useful for testing or reset)
 */
export const clearParentOrigin = (): void => {
  if (typeof window !== 'undefined') {
    delete (window as any).__WIDGET_PARENT_ORIGIN__;
    delete (window as any).__WIDGET_DEBUG_PARENT_ORIGIN__;
    console.warn('🧹 [ParentOrigin] Cleared stored parent origin');
  }
};

/**
 * DEBUGGING: Parent Origin is exposed on window object for inspection
 * 
 * Available in both development and production:
 * 
 * 1. window.__WIDGET_DEBUG_PARENT_ORIGIN__
 *    - The detected parent origin (from postMessage, referrer, or opener)
 *    - Available immediately after widget loads
 * 
 * 2. window.__WIDGET_DEBUG_X_PARENT_ORIGIN__
 *    - The value sent in X-Parent-Origin header to backend
 *    - Updated on each API call
 * 
 * Usage in browser console:
 *   console.log(window.__WIDGET_DEBUG_PARENT_ORIGIN__);
 *   console.log(window.__WIDGET_DEBUG_X_PARENT_ORIGIN__);
 * 
 * Note: console.warn() is used for logging (persists in production builds)
 */

